import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button';

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Drawer from the right: opaque themed panel (MB); RS uses blurred/dimmed overlay sibling.
 * Closes when a link (anchor) inside the content is clicked (navigation).
 */
export default function HamburgerMenu({ isOpen, onClose, children }: HamburgerMenuProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('a[href]')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="sb-drawer-overlay"
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        className="sb-drawer-panel sb-drawer-panel--right sb-drawer-panel--open"
        role="dialog"
        aria-modal="true"
        aria-label="Menü"
      >
        <header className="sb-drawer-panel-header d-flex align-items-center justify-content-between px-3 py-3 border-bottom border-secondary-subtle">
          <span className="fw-bold text-primary">Menü</span>
          <Button
            type="button"
            className="btn btn-link p-2"
            style={{ color: 'var(--sb-text)' }}
            onClick={onClose}
            aria-label="Menüyü kapat"
          >
            <i className="fas fa-times fa-lg" />
          </Button>
        </header>
        <div
          className="sb-drawer-panel-body overflow-auto px-3 flex-grow-1 min-h-0 pt-2"
          onClick={handleContentClick}
        >
          {children}
        </div>
      </aside>
    </>,
    document.body
  );
}
